import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import Config from '../config';
import { EMPTY_STRING } from '../constants/constant';
import {
  APP_INSIGHTS_SEVERITY,
  CORRELATION_FIELDS,
  TELEMETRY_STORAGE_KEYS,
} from '../constants/telemetryConstants';
import {
  appInsights,
  getClientInstallationId,
  getSessionId,
} from './appInsights';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogMetadata = Record<string, unknown>;

type LoggerOptions = {
  captureSentryMessage?: boolean;
};

type BrowserLogger = Pick<Console, 'debug' | 'info' | 'warn' | 'error'>;

const LOG_LEVEL_WEIGHT: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const MAX_SANITIZE_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 40;
const DEV_ENV_HINTS = new Set([
  'development',
  'dev',
  'sandbox',
  'local',
  'localhost',
]);
const SENSITIVE_KEY_PATTERN =
  /(^|_|-|\.)(authorization|password|passwd|secret|token|cookie|session|email|phone|ssn|otp|code)($|_|-|\.)/i;
const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOGGER_CONSOLE_PREFIX = '[assetron-log]';

const isBrowser = (): boolean => globalThis.window !== undefined;

const readSessionStorage = (key: string): string | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    return globalThis.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionStorage = (key: string, value: string): void => {
  if (!isBrowser()) {
    return;
  }

  try {
    globalThis.sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in restricted browser contexts.
  }
};

const getRuntimeEnvironment = (): string => {
  return (
    Config.ENVIRONMENT?.trim().toLowerCase() ??
    process.env.NODE_ENV?.trim().toLowerCase() ??
    'unknown'
  );
};

const isLocalRuntime = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  const hostname = globalThis.location.hostname.toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const isVerboseLoggingEnabled = (): boolean => {
  const explicit = Config.VERBOSE_LOGGING?.trim().toLowerCase();
  if (explicit === 'true') {
    return true;
  }
  if (explicit === 'false') {
    return false;
  }

  return isLocalRuntime() || DEV_ENV_HINTS.has(getRuntimeEnvironment());
};

const resolveMinimumLogLevel = (): LogLevel => {
  const configuredLevel = Config.LOG_LEVEL?.trim().toLowerCase();
  if (
    configuredLevel === 'debug' ||
    configuredLevel === 'info' ||
    configuredLevel === 'warn' ||
    configuredLevel === 'error'
  ) {
    return configuredLevel;
  }

  if (isVerboseLoggingEnabled()) {
    return 'debug';
  }

  return getRuntimeEnvironment() === 'production' ? 'warn' : 'info';
};

const shouldEmitLevel = (level: LogLevel): boolean =>
  LOG_LEVEL_WEIGHT[level] >= LOG_LEVEL_WEIGHT[resolveMinimumLogLevel()];

const sanitizeStringValue = (
  value: string,
  keyHint?: string,
): string | Record<string, unknown> => {
  const trimmed = value.trim();
  if (keyHint && SENSITIVE_KEY_PATTERN.test(keyHint)) {
    return '[REDACTED]';
  }
  if (JWT_PATTERN.test(trimmed)) {
    return '[REDACTED_TOKEN]';
  }
  if (EMAIL_PATTERN.test(trimmed)) {
    return '[REDACTED_EMAIL]';
  }
  if (trimmed.length > 500) {
    return {
      preview: `${trimmed.slice(0, 200)}...`,
      length: trimmed.length,
    };
  }

  return value;
};

export const summarizePayload = (value: unknown): unknown => {
  if (value == null) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return EMPTY_STRING;
    }

    try {
      return summarizePayload(JSON.parse(trimmed));
    } catch {
      return sanitizeStringValue(trimmed);
    }
  }

  if (value instanceof FormData) {
    const keys = Array.from(value.keys());
    return {
      type: 'FormData',
      keyCount: keys.length,
      keys: keys.slice(0, 10),
    };
  }

  if (value instanceof URLSearchParams) {
    const entries = Array.from(value.keys());
    return {
      type: 'URLSearchParams',
      keyCount: entries.length,
      keys: entries.slice(0, 10),
    };
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }

  if (Array.isArray(value)) {
    return {
      type: 'array',
      itemCount: value.length,
      sample: value.slice(0, 5).map((item) => summarizePayload(item)),
    };
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return {
      type: 'object',
      keyCount: entries.length,
      keys: entries.slice(0, 10).map(([key]) => key),
    };
  }

  return value;
};

const sanitizeForLog = (
  value: unknown,
  keyHint?: string,
  depth = 0,
): unknown => {
  if (value == null) {
    return value;
  }

  if (depth >= MAX_SANITIZE_DEPTH) {
    return summarizePayload(value);
  }

  if (typeof value === 'string') {
    return sanitizeStringValue(value, keyHint);
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: isVerboseLoggingEnabled() && value.stack ? value.stack : undefined,
    };
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((entry) => sanitizeForLog(entry, keyHint, depth + 1));
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>,
    ).slice(0, MAX_OBJECT_KEYS)) {
      result[key] = sanitizeForLog(entry, key, depth + 1);
    }
    return result;
  }

  return String(value);
};

const getOrCreateFrontendCorrelationId = (): string => {
  const stored =
    readSessionStorage(TELEMETRY_STORAGE_KEYS.CORRELATION_ID)?.trim() ??
    EMPTY_STRING;
  if (stored) {
    return stored;
  }

  const generated = uuidv4();
  writeSessionStorage(TELEMETRY_STORAGE_KEYS.CORRELATION_ID, generated);
  return generated;
};

const getCurrentUserId = (): string | null =>
  readSessionStorage(TELEMETRY_STORAGE_KEYS.USER_ID);

const getCurrentOrganizationId = (): string | null =>
  readSessionStorage(TELEMETRY_STORAGE_KEYS.ORG_ID);

const buildBaseMetadata = (): LogMetadata => ({
  environment: Config.ENVIRONMENT,
  appVersion: Config.APP_VERSION,
  sessionId: getSessionId(),
  clientInstallationId: getClientInstallationId(),
  [CORRELATION_FIELDS.USER_ID]: getCurrentUserId(),
  [CORRELATION_FIELDS.ORG_ID]: getCurrentOrganizationId(),
  [CORRELATION_FIELDS.SESSION_ID]: getSessionId(),
  [CORRELATION_FIELDS.CLIENT_ID]: getClientInstallationId(),
  frontendCorrelationId: getOrCreateFrontendCorrelationId(),
  operationId: appInsights.context?.telemetryTrace?.traceID ?? undefined,
  pathname: isBrowser() ? globalThis.location.pathname : undefined,
});

const toAppInsightsSeverity = (level: LogLevel): number => {
  switch (level) {
    case 'debug':
      return APP_INSIGHTS_SEVERITY.VERBOSE;
    case 'info':
      return APP_INSIGHTS_SEVERITY.INFORMATION;
    case 'warn':
      return APP_INSIGHTS_SEVERITY.WARNING;
    case 'error':
      return APP_INSIGHTS_SEVERITY.ERROR;
    default:
      return APP_INSIGHTS_SEVERITY.INFORMATION;
  }
};

const toSentryLevel = (level: LogLevel): Sentry.SeverityLevel => {
  switch (level) {
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    case 'warn':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
};

const getConsoleMethod = (level: LogLevel): keyof BrowserLogger => {
  switch (level) {
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    case 'warn':
      return 'warn';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
};

const emitConsoleLog = (
  level: LogLevel,
  scope: string,
  message: string,
  metadata: LogMetadata,
): void => {
  if (!isVerboseLoggingEnabled()) {
    return;
  }

  const consoleMethod = getConsoleMethod(level);
  const browserConsole = console as BrowserLogger;
  browserConsole[consoleMethod](
    `${LOGGER_CONSOLE_PREFIX} ${scope}:${message}`,
    metadata,
  );
};

const emitAppInsightsTrace = (
  level: LogLevel,
  scope: string,
  message: string,
  metadata: LogMetadata,
): void => {
  appInsights.trackTrace({
    message: `${scope}:${message}`,
    severityLevel: toAppInsightsSeverity(level),
    properties: metadata,
  });
};

const emitSentryData = (
  level: LogLevel,
  scope: string,
  message: string,
  metadata: LogMetadata,
  error?: Error,
  options?: LoggerOptions,
): void => {
  Sentry.addBreadcrumb({
    category: scope,
    message,
    level: toSentryLevel(level),
    data: metadata,
  });

  if (level === 'error') {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags: { scope },
        extra: metadata,
      });
      return;
    }

    Sentry.captureMessage(`${scope}:${message}`, {
      level: 'error',
      tags: { scope },
      extra: metadata,
    });
    return;
  }

  if (level === 'warn' && options?.captureSentryMessage) {
    Sentry.captureMessage(`${scope}:${message}`, {
      level: 'warning',
      tags: { scope },
      extra: metadata,
    });
  }
};

const emitLog = (
  level: LogLevel,
  scope: string,
  baseMetadata: LogMetadata,
  message: string,
  metadata?: LogMetadata,
  options?: LoggerOptions,
): void => {
  if (!shouldEmitLevel(level)) {
    return;
  }

  const originalError =
    metadata?.error instanceof Error ? metadata.error : undefined;
  const safeMetadata = sanitizeForLog({
    ...buildBaseMetadata(),
    ...baseMetadata,
    ...metadata,
  }) as LogMetadata;

  emitConsoleLog(level, scope, message, safeMetadata);
  emitAppInsightsTrace(level, scope, message, safeMetadata);
  emitSentryData(level, scope, message, safeMetadata, originalError, options);
};

export type Logger = {
  debug: (message: string, metadata?: LogMetadata) => void;
  info: (message: string, metadata?: LogMetadata) => void;
  warn: (
    message: string,
    metadata?: LogMetadata,
    options?: LoggerOptions,
  ) => void;
  error: (
    message: string,
    metadata?: LogMetadata,
    options?: LoggerOptions,
  ) => void;
  child: (suffix: string, childMetadata?: LogMetadata) => Logger;
};

export const createLogger = (
  scope: string,
  baseMetadata: LogMetadata = {},
): Logger => ({
  debug: (message, metadata) =>
    emitLog('debug', scope, baseMetadata, message, metadata),
  info: (message, metadata) =>
    emitLog('info', scope, baseMetadata, message, metadata),
  warn: (message, metadata, options) =>
    emitLog('warn', scope, baseMetadata, message, metadata, options),
  error: (message, metadata, options) =>
    emitLog('error', scope, baseMetadata, message, metadata, options),
  child: (suffix, childMetadata = {}) =>
    createLogger(`${scope}.${suffix}`, {
      ...baseMetadata,
      ...childMetadata,
    }),
});
